import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { InnerBlock } from "@/components/InnerBlock";

const LAST_UPDATED = "2 September 2026";
const CR_NUMBER = "1653208";

const LAWFUL_BASIS_ROWS = [
  ["Reply to your enquiry and prepare a proposal", "To respond and quote", "Your consent, and steps taken at your request before entering a contract"],
  ["Deliver services you have engaged us for", "To perform our contract", "Performance of a contract"],
  ["Send you updates or a requested guide", "To keep you informed", "Your consent, withdrawable at any time"],
  ["Assess a job application", "To consider you for a role", "Steps taken at your request"],
  ["Operate, secure and improve the website", "To keep the site working and safe", "Our legitimate interest in running our business securely"],
  ["Keep accounting and tax records", "Because we are required to", "Compliance with a legal obligation"],
  ["Detect and prevent fraud or misuse", "To protect our systems and clients", "Our legitimate interest in security"],
];

const RETENTION_ROWS = [
  ["Enquiries that do not become projects", "24 months from last contact"],
  ["Client records and correspondence", "Duration of the engagement, then 10 years"],
  ["Accounting and tax records", "As required by Omani law"],
  ["Job applications (unsuccessful)", "12 months, unless you ask us to keep them longer"],
  ["Subscriptions", "Until you unsubscribe"],
  ["Website analytics", "26 months"],
  ["Server and security logs", "12 months"],
];

export default function PrivacyPolicy() {
  return (
    <div data-testid="privacy-policy-page">
      <Seo title="Privacy Policy" description="How Wafa Solutions LLC collects, uses and protects personal data under Oman's PDPL." />

      <PageHero
        eyebrow="Legal"
        h1="Privacy Policy"
        intro={`Last updated: ${LAST_UPDATED} · Effective from: ${LAST_UPDATED}`}
        breadcrumbs={[{ label: "Home", to: "/" }, { label: "Privacy Policy" }]}
      />

      <InnerBlock tint="white">
        <article className="mx-auto max-w-3xl space-y-10 text-base leading-relaxed text-wafa-ink/85 lg:text-lg">

          <section>
            <h2 className="font-heading text-2xl font-semibold text-wafa-deep">1. Who we are</h2>
            <p className="mt-4">
              Wafa Solutions LLC ("Wafa Solutions", "we", "us") is a technology company registered in the Sultanate of Oman under commercial registration number {CR_NUMBER}, and the technology partner to Altalib Group. For the purposes of the Personal Data Protection Law issued under Royal Decree 6/2022 and its Executive Regulations issued under Ministerial Decision 34/2024, we are the controller of the personal data described in this policy. This policy explains what we collect, why, how long we keep it, and what rights you have.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-wafa-deep">2. Contacting us about your data</h2>
            <p className="mt-4">
              For any question about this policy or your data, or to exercise your rights, contact us at <a href="mailto:support@wafa.om" className="underline underline-offset-2">support@wafa.om</a>. We are in the process of formally appointing a named Data Protection Officer; until then, all data protection queries are handled by our team at the address above.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-wafa-deep">3. What we collect</h2>
            <ul className="mt-4 space-y-3">
              <li><strong>When you contact us or request a quote —</strong> your name, company name, email address, telephone or WhatsApp number, and whatever you choose to tell us about your project, including any file you upload.</li>
              <li><strong>When you subscribe or download a guide —</strong> your name and email address.</li>
              <li><strong>When you apply for a job —</strong> your name, contact details, CV, work history and anything else you send us.</li>
              <li><strong>When you visit our website —</strong> your IP address, browser type and version, device type, operating system, referring page, the pages you view, and the time and duration of your visit, collected through cookies and similar technologies.</li>
              <li><strong>When you become a client —</strong> contact details for your staff who work with us, billing information, and correspondence relating to the services.</li>
              <li><strong>What we do not collect —</strong> we do not deliberately collect sensitive personal data through this website, including data revealing health, biometric identifiers or genetic data. Please do not send us sensitive personal data through the website forms. Where a client engagement genuinely requires processing sensitive personal data, we address it separately under a written agreement and obtain any permit required from the Ministry before processing begins.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-wafa-deep">4. Why we collect it, and on what basis</h2>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full min-w-[560px] border-collapse text-sm">
                <thead>
                  <tr className="bg-wafa-cream/60 text-start text-wafa-deep">
                    <th className="border border-wafa-deep/10 p-3 font-semibold">Purpose</th>
                    <th className="border border-wafa-deep/10 p-3 font-semibold">Why</th>
                    <th className="border border-wafa-deep/10 p-3 font-semibold">Lawful basis</th>
                  </tr>
                </thead>
                <tbody>
                  {LAWFUL_BASIS_ROWS.map((r, i) => (
                    <tr key={i} className="align-top">
                      <td className="border border-wafa-deep/10 p-3">{r[0]}</td>
                      <td className="border border-wafa-deep/10 p-3">{r[1]}</td>
                      <td className="border border-wafa-deep/10 p-3">{r[2]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4">
              <strong>Consent:</strong> Where we rely on your consent, it is explicit, specific and recorded. You can withdraw it at any time by emailing <a href="mailto:support@wafa.om" className="underline underline-offset-2">support@wafa.om</a>, and withdrawal does not affect processing carried out before you withdrew.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-wafa-deep">5. Where your data is stored</h2>
            <p className="mt-4">
              We store personal data on infrastructure operated by our hosting and service providers. The tools we use to run this website and our business communications are:
            </p>
            <ul className="mt-3 list-disc space-y-1 ps-6">
              <li><strong>Namecheap Private Email</strong> — our business email and SMTP for enquiry responses. Data is processed in Namecheap's infrastructure.</li>
              <li><strong>Google Analytics 4</strong> — website analytics, loaded only after you accept analytics cookies. Data is processed in Google's infrastructure.</li>
              <li><strong>Emergent</strong> — the platform this website is hosted on. Application traffic is processed in Emergent's infrastructure.</li>
            </ul>
            <p className="mt-4">We do not claim in-country hosting in Oman.</p>
            <p className="mt-4">Where personal data is transferred outside the Sultanate of Oman:</p>
            <ul className="mt-3 list-disc space-y-1 ps-6">
              <li>we transfer only what is necessary for that specific purpose;</li>
              <li>we assess the level of data protection available in the receiving country before transferring;</li>
              <li>we put contractual protections in place with the provider;</li>
              <li>we obtain your consent where the law requires it; and</li>
              <li>we do not transfer sensitive personal data outside Oman without the approval of the Ministry.</li>
            </ul>
            <p className="mt-4">You can ask us at <a href="mailto:support@wafa.om" className="underline underline-offset-2">support@wafa.om</a> at any time for a current list of where your data is processed.</p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-wafa-deep">6. Who we share it with</h2>
            <p className="mt-4">
              We do not sell your personal data, and we do not share it for anyone else's marketing. We share it only with:
            </p>
            <ul className="mt-3 list-disc space-y-1 ps-6">
              <li>our own team, where they need it to do their job — our people work from Oman, India, the United States and the Philippines, and are bound by confidentiality obligations;</li>
              <li>service providers who help us operate, under contracts restricting what they may do with it;</li>
              <li>professional advisers such as accountants and lawyers where necessary;</li>
              <li>authorities, where we are required by Omani law to disclose, or to establish or defend a legal claim.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-wafa-deep">7. How long we keep it</h2>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full min-w-[420px] border-collapse text-sm">
                <thead>
                  <tr className="bg-wafa-cream/60 text-start text-wafa-deep">
                    <th className="border border-wafa-deep/10 p-3 font-semibold">Category</th>
                    <th className="border border-wafa-deep/10 p-3 font-semibold">Retention</th>
                  </tr>
                </thead>
                <tbody>
                  {RETENTION_ROWS.map((r, i) => (
                    <tr key={i} className="align-top">
                      <td className="border border-wafa-deep/10 p-3">{r[0]}</td>
                      <td className="border border-wafa-deep/10 p-3">{r[1]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4">When a retention period ends we delete the data or irreversibly anonymise it.</p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-wafa-deep">8. Your rights</h2>
            <p className="mt-4">Under the Personal Data Protection Law you have the right to:</p>
            <ul className="mt-3 list-disc space-y-1 ps-6">
              <li>be informed about how your personal data is processed;</li>
              <li>access your personal data and obtain a copy;</li>
              <li>correct or update data that is inaccurate or incomplete;</li>
              <li>request deletion;</li>
              <li>withdraw consent at any time where processing is based on consent;</li>
              <li>object to processing in the circumstances the law allows;</li>
              <li>be notified of a personal data breach affecting you;</li>
              <li>complain to the Ministry if you are not satisfied with our response.</li>
            </ul>
            <p className="mt-4">
              <strong>How to exercise them:</strong> email <a href="mailto:support@wafa.om" className="underline underline-offset-2">support@wafa.om</a> with what you want and enough information for us to identify you. We may ask you to verify your identity — this protects you.
            </p>
            <p className="mt-4">
              <strong>Our response time:</strong> we will respond within forty-five (45) days of receiving your request, as required by the Executive Regulations. We do not charge for this.
            </p>
            <p className="mt-4">
              If you are not satisfied, you may complain to the Personal Data Protection Department at the Ministry of Transport, Communications and Information Technology. Please contact us first if you can — most issues are faster to fix directly.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-wafa-deep">9. How we protect it</h2>
            <p className="mt-4">We apply technical and organisational measures appropriate to the risk, including:</p>
            <ul className="mt-3 list-disc space-y-1 ps-6">
              <li>encryption of data in transit and at rest;</li>
              <li>role-based access control so staff see only what they need;</li>
              <li>audit logging of access to personal data;</li>
              <li>multi-factor authentication on administrative systems;</li>
              <li>regular patching and vulnerability management;</li>
              <li>automated backups with tested restoration;</li>
              <li>staff confidentiality obligations and training;</li>
              <li>vendor security assessment before we engage a supplier.</li>
            </ul>
            <p className="mt-4">
              No system is perfectly secure, and we do not claim otherwise. What we commit to is proportionate protection, and honest and prompt notification if something goes wrong.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-wafa-deep">10. If there is a breach</h2>
            <p className="mt-4">
              If a personal data breach occurs that may pose a risk to your rights, we will notify the Ministry within seventy-two (72) hours of becoming aware of it. Where the breach is likely to cause you serious harm, we will notify you as well, and tell you what happened, what data was involved, what we are doing about it, and what you should do. We maintain a record of personal data breaches, available to the Ministry on request.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-wafa-deep">11. Cookies</h2>
            <p className="mt-4">
              See our <a href="/cookie-policy" className="underline underline-offset-2">Cookie Policy</a>.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-wafa-deep">12. Children</h2>
            <p className="mt-4">
              This website is not directed at children. We do not knowingly collect personal data from anyone under the age of 18 through this site. Where a client project involves children's data, we address it separately, including obtaining guardian consent as the Executive Regulations require. If you believe a child has provided us personal data, contact our DPO and we will delete it.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-wafa-deep">13. Links to other sites</h2>
            <p className="mt-4">
              Our website links to other websites we do not control. This policy does not apply to them.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-wafa-deep">14. Changes</h2>
            <p className="mt-4">
              We may update this policy. The date at the top shows when it last changed. Where a change materially affects your rights, we will tell you directly.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-wafa-deep">15. Contact</h2>
            <p className="mt-4">
              Wafa Solutions LLC, Sultanate of Oman.<br />
              Email <a href="mailto:support@wafa.om" className="underline underline-offset-2">support@wafa.om</a>, or use the "Chat on WhatsApp" button on any page.
            </p>
          </section>
        </article>
      </InnerBlock>
    </div>
  );
}
